import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './loginPage';
import AdminPanel from './adminPanel';
import Settings from './settings/Settings';
import Discovery from './discovery';
import CallsListPage from './calls/CallsListPage';
import CallPage from './calls/CallPage';
import AppShell from './components/AppShell';
import { PrereleaseRoute } from './shell/PrereleaseRoute';
import { VoiceDictation } from './shell/VoiceDictation';
import './App.css';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, RootState } from './store';
import AuthCallback from './components/AuthCallback';
import SilentAuthCallback from './components/SilentAuthCallback';
import { refreshAndSendSessionToExtension } from './auth/extensionSession';
import { shouldShowExtensionOffBanner } from './extensionHandshake';

/**
 * The dev-channel downloads page, public (no auth). Production never registers
 * the route, and because MODE is a build-time constant the page and its chunk
 * are dropped from the production bundle entirely.
 */
/** The onboarding tour, in its own chunk: most sessions never open it. */
const TourRoot = lazy(() => import('./tour/TourRoot'));

const ValuePage = lazy(() => import('./value/ValuePage'));

const Downloads =
  import.meta.env.MODE !== 'production'
    ? lazy(() => import('./downloads/Downloads'))
    : null;

const App = () => {
  const { isUserAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );

  const dispatch = useDispatch<Dispatch>();

  // Run a function on every full page load when authenticated
  useEffect(() => {
    const onHardLoad = async () => {
      try {
        // The OAuth callback is storing a brand-new sign-in right now; refreshing
        // beside it could clear or overwrite what it writes.
        if (!isUserAuthenticated || window.location.pathname.startsWith('/auth/')) {
          return;
        }
        console.log('Hard refresh detected: running on-load hook');
        const outcome = await refreshAndSendSessionToExtension();
        if (outcome.kind === 'signed_out') {
          dispatch.auth.setUserAuthenticated(false);
        } else if (
          outcome.kind === 'sent' &&
          // No answer from an extension that answered here before: a Chrome
          // update has most likely switched it off. The app shell says so.
          shouldShowExtensionOffBanner(outcome.answered)
        ) {
          dispatch.user.setExtensionSwitchedOff(true);
        }
      } catch (e) {
        console.error('onHardLoad error', e);
      }
    };

    // Trigger only on initial mount (i.e., full page load)
    onHardLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <VoiceDictation>
      <BrowserRouter>
        <Routes>
          <Route path="" element={<Navigate replace to="/home" />}></Route>
          <Route path="/" element={<Navigate replace to="/home" />}></Route>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/silent-callback" element={<SilentAuthCallback />} />
          <Route path="/login" element={<LoginPage />} />
          {Downloads && (
            <Route
              path="/downloads"
              element={
                <Suspense fallback={null}>
                  <Downloads />
                </Suspense>
              }
            />
          )}
          {/* Legacy path — chat now lives at /home. */}
          <Route path="/discovery" element={<Navigate replace to="/home" />} />
          {/* Every signed-in page, inside one shell that stays mounted across
              navigation (one /get_user_info, one extension check). */}
          <Route
            element={
              isUserAuthenticated ? <AppShell /> : <Navigate replace to="/login" />
            }
          >
            {/* Home IS the Chat-with-Dooglie conversation now. */}
            <Route path="/home" element={<Discovery />} />
            <Route
              path="/calls"
              element={
                <PrereleaseRoute feature="calls">
                  <CallsListPage />
                </PrereleaseRoute>
              }
            />
            <Route
              path="/calls/:botId"
              element={
                <PrereleaseRoute feature="calls">
                  <CallPage />
                </PrereleaseRoute>
              }
            />
            <Route path="/admin" element={<AdminPanel />} />
            <Route
              path="/value"
              element={
                <Suspense fallback={null}>
                  <ValuePage />
                </Suspense>
              }
            />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="/onboarding"
              element={
                <Suspense fallback={null}>
                  <TourRoot />
                </Suspense>
              }
            />
          </Route>
          <Route path="/error" element={<div>Error Page</div>} />
        </Routes>
      </BrowserRouter>
    </VoiceDictation>
  );
};

export default App;
